/** /orders/ — ops-wide order search across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to `/bookings/`, but
 * over the Order model (see OrderFilterNode in the OpenAPI spec).
 *
 * Autocomplete suggestions reuse the shared Bookings autocomplete endpoints
 * (tenant/tour/user/tag/category) via `makeSuggestionFetcher` — re-exported
 * from `@/api/bookings` so the Orders namespace config has a single import.
 */
import type { WireNode } from '@/components/QuerySearch';

import { apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { BookingTag, NamedRef, UserRef } from './bookings';
import type { ApiRequestListItem } from './apiRequests';
import type { ImportRunListItem } from './importRuns';
import type { PaginatedResponse } from './users';
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface OrderTenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  /** Set when this tenant is a franchise licensee (child TO). */
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface OrderTourRef {
  id: number;
  name: string;
}

export interface OrderDepartureRef {
  id: number;
  name: string;
}

/** One booking inside an order (tour + date + departure), ordered by start. */
export interface OrderBooking {
  id: number;
  tour: OrderTourRef | null;
  date: string | null;
  start_ts: string | null;
  departure: OrderDepartureRef | null;
}

export interface OrderContact {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  country: string | null;
}

export interface OrderListItem {
  id: number;
  tenant: OrderTenantRef;
  order_ref: string | null;
  custom_id: string | null;
  supplier_ref: string | null;
  reseller_ref: string | null;
  booking_state: number;
  booking_state_display: string;
  active: boolean;
  contact: OrderContact | null;
  integration: string | null;
  restech_account: string | null;
  currency: string | null;
  /** Number of decimal places for the price fields' minor units. */
  currency_precision: number | null;
  /** Prices are in MINOR units (e.g. cents) — divide by 10^currency_precision. */
  price_net: number | null;
  price_original: number | null;
  price_retail: number | null;
  tax_amount: number | null;
  created_ts: string | null;
  updated_ts: string | null;
  /** Earliest booking start. */
  start_datetime: string | null;
  end_datetime: string | null;
  booking_count: number;
  bookings: OrderBooking[];
  /** Order-level tags (CP-8095) — facts about the whole order, distinct from
   *  the per-booking tags under `bookings[].tags`. */
  tags: BookingTag[];
}

/** The list endpoint's paginated envelope carries a capped-count flag. */
export type OrderListResponse = PaginatedResponse<OrderListItem> & {
  /** Present in `count_mode=sync` only: the sync count is capped at 10 000
   *  probed rows. The async/deferred on-demand total is exact and uncapped, so
   *  there is nothing to cap and the key is absent. */
  count_is_capped?: boolean;
};

export interface OrderListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default (`deferred` on this grid) — that omission is what keeps a cold
   *  first load free of a COUNT. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(p: OrderListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  return usp.toString();
}

export async function list(p: OrderListParams = {}): Promise<OrderListResponse> {
  const qs = toQuery(p);
  return apiGet(`/orders/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** Total only, for the grid's on-demand row count. `async` so the count runs
 *  in Celery: an exact, uncapped total that never occupies a web worker. */
export async function count(p: OrderListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...p, count_only: true, count_mode: 'async' });
  return apiGet(`/orders/?${qs}`);
}

// ---------------------------------------------------------------------------
// Order detail + imports (overview panel / master-detail). Shared refs reused
// from api/bookings.ts (NamedRef/UserRef/BookingTag/ImportRun).
// ---------------------------------------------------------------------------

/** One booking in an order, for the Order overview bookings grid. */
export interface OrderBookingRow {
  id: number;
  tour: NamedRef | null;
  date: string | null;
  start_ts: string | null;
  /** IANA zone of the booking's tour — its local date and time are read in it. */
  start_timezone?: string | null;
  departure: NamedRef | null;
  user: UserRef | null;
  supplier_ref: string | null;
  reseller_ref: string | null;
  /** Hidden by default in the grid. */
  custom_id: string | null;
  /** The booking's second id in its booking system (Ventrata's native id); hidden by default. */
  alt_custom_id: string | null;
  booking_state: number;
  booking_state_display: string;
  people_count: number | null;
  tags: BookingTag[];
}

export interface OrderDetail {
  id: number;
  tenant: OrderTenantRef | null;
  user: { id: number; first_name: string | null; last_name: string | null; email: string | null } | null;
  order_ref: string | null;
  custom_id: string | null;
  supplier_ref: string | null;
  reseller_ref: string | null;
  booking_state: number;
  booking_state_display: string;
  active: boolean;
  contact: OrderContact | null;
  integration: string | null;
  restech_account: string | null;
  currency: string | null;
  currency_precision: number | null;
  price_net: number | null;
  price_original: number | null;
  price_retail: number | null;
  tax_amount: number | null;
  settlement_method?: string | null;
  email_receipt?: boolean | null;
  created_ts: string | null;
  updated_ts: string | null;
  remote_created_ts: string | null;
  remote_updated_ts: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  booking_count: number;
  bookings: OrderBookingRow[];
  /** Order-level tags (CP-8095) — facts about the whole order, distinct from
   *  the per-booking tags under `bookings[].tags`. */
  tags: BookingTag[];
}

/** The order detail's query key — the record page, the grid's detail panel and the
 *  booking page's order line share it (an order save re-seeds it). */
export const orderDetailKey = (id: number) => ['orders', 'detail', id] as const;

/** GET /orders/{id}/ — rich detail powering the order record page and the grid panel. */
export async function retrieve(id: number): Promise<OrderDetail> {
  return apiGet(`/orders/${id}/`);
}

/** GET /orders/{id}/imports/ */
export async function listImportRuns(id: number): Promise<ImportRunListItem[]> {
  return apiGet(`/orders/${id}/imports/`);
}

/** GET /orders/{id}/api_requests/ — the webhook / API requests that touched
 *  this order (the /api_requests/ grid's row shape). */
export async function listApiRequests(id: number): Promise<ApiRequestListItem[]> {
  return apiGet(`/orders/${id}/api_requests/`);
}

// The Orders namespace autocompletes are the SAME shared Bookings autocomplete
// endpoints; re-export the fetcher factory so ordersNamespaces has one import.
export { makeSuggestionFetcher, type SuggestKind } from './bookings';

// ---- AI diagnostics (perm: console_entity_ai; shared entity_diagnose tool) --

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/orders/diagnose/launch_context/');
}

/** POST /orders/{id}/diagnose/ — same protocol as the bookings variant. */
export async function startDiagnose(
  id: number,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/orders/${id}/diagnose/`, body);
}
